import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Mail, MessageSquare, UserPlus, Loader2, Sparkles } from "lucide-react";
import { MatchRing } from "@/components/ui-bits";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchCandidate = async () => {
      try {
        const { data } = await api.get("/recruiter/candidates");
        const list = data?.candidates || [];
        const found = list.find((x) => String(x.id) === String(id)) || list[0];
        if (active) {
          setCandidate(found || null);
        }
      } catch (_) {
        if (active) setCandidate(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchCandidate();
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-[350px] items-center justify-center rounded-3xl border border-dashed border-border">
        <Loader2 className="h-6 w-6 animate-spin text-electric" />
        <span className="ml-2 text-xs text-muted-foreground">Loading candidate profile…</span>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        <p className="font-heading text-lg font-bold">Candidate not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 cv-btn-ghost border border-border text-xs">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to search
        </button>
      </div>
    );
  }

  const c = candidate;

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to search
      </button>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Candidate Identity Card */}
        <div className="rounded-3xl border border-border bg-gradient-to-br from-violet via-electric to-aqua p-[1.5px] shadow-float">
          <div className="h-full rounded-3xl bg-card p-6 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-electric to-violet font-heading text-2xl font-bold text-white shadow-xs">
              {(c.name || "C").charAt(0).toUpperCase()}
            </div>
            <p className="mt-4 font-heading text-xl font-extrabold">{c.name}</p>
            <p className="text-xs text-muted-foreground font-medium mt-0.5">{c.role}</p>
            <p className="mt-1 flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 text-electric" />
              {c.location}
            </p>

            <div className="my-5 flex justify-center">
              <MatchRing value={c.match || 88} size={96} stroke={9} color="hsl(var(--chart-4))" />
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => toast.success(`Invited ${c.name} to interview pipeline`)}
                className="cv-btn-primary text-xs flex items-center justify-center gap-1.5"
              >
                <UserPlus className="h-4 w-4" /> Add to Pipeline
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => toast.success(`Direct message opened for ${c.name}`)}
                  className="cv-btn-ghost flex-1 border border-border text-xs justify-center flex items-center gap-1"
                >
                  <MessageSquare className="h-3.5 w-3.5" /> Message
                </button>
                <button
                  onClick={() => {
                    window.location.href = `mailto:${c.email || "candidate@example.com"}?subject=Career Opportunity`;
                    toast.success("Opening email client");
                  }}
                  className="cv-btn-ghost flex-1 border border-border text-xs justify-center flex items-center gap-1"
                >
                  <Mail className="h-3.5 w-3.5" /> Email
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Candidate Detail Content */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-surface p-6 shadow-sm">
            <p className="label-caps mb-3 text-electric flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Verified Skills & Competencies
            </p>
            <div className="flex flex-wrap gap-2">
              {(c.skills || ["Strategy", "Leadership", "Analytics"]).map((s) => (
                <span
                  key={s}
                  className="rounded-full bg-secondary border border-border px-3 py-1.5 text-xs font-semibold"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="card-surface p-6 shadow-sm">
            <p className="label-caps mb-4 text-violet">Experience & Qualifications</p>
            <div className="space-y-3">
              <div className="rounded-xl border border-border p-4 bg-secondary/30">
                <p className="font-bold text-sm text-foreground">{c.role}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{c.exp} experience · {c.location}</p>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                  Verified Career Profile with extensive demonstrated track record delivering impact, collaborating cross-functionally, and scaling domain initiatives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
